import * as React from 'react';
import FilterLayout from 'components/common/searchfilter/FilterLayout';
import DateRangeFilter from 'components/common/searchfilter/contents/DateRangeFilter';
import FilterElement from 'components/common/searchfilter/FilterElement';
import PayMethodFilters from 'components/plansales/billing/searchfilter/filtercontents/PayMethodFilters';
import KeywordInput from 'components/common/searchfilter/contents/KeywordInput';
import { SearchContentBlock } from 'components/plansales/billing/searchfilter/FilterContents';
import GroupPlanTypeFilter from 'components/plansales/invoice/invoiceHistory/GroupPlanTypeFilter';
import { GroupPlanType } from 'types/plansales/plan';
import { InvoicePaymentStatus, PaymentMethod } from 'types/plansales/billing';
import { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { SearchDateTerms } from 'types/common/searchfilter';
import keyboardKey from 'keyboard-key';
import PayStatusFilters from '../../billing/searchfilter/filtercontents/PayStatusFilters';

export interface InvoiceHistorySearchFilterValues {
  dateInterval: string[];
  dateShortCut: SearchDateTerms;
  planType: {
    [GroupPlanType.STANDARD]: boolean;
    [GroupPlanType.PREMIUM]: boolean;
    [GroupPlanType.ADVANCED]: boolean; // TODO: admin 개발 기간이 좀 지나 더이상 adv 플랜은 쌓일 일이 없기에 피오나께 이 옵션 유지하는지 확인하자
  };
  payStatus: {
    [InvoicePaymentStatus.Completed]: boolean;
    [InvoicePaymentStatus.Pending]: boolean;
    [InvoicePaymentStatus.Uncollectible]: boolean;
    [InvoicePaymentStatus.Refunded]: boolean;
  };
  paymentMethod: {
    [PaymentMethod.STRIPE]: boolean;
    [PaymentMethod.CREDITS]: boolean;
  };
  keyword: string;
  pageNumber: number;
}
interface Props {
  resetSearchFilter: () => void;
  fetchSearchData: () => void;
  selectedFilterValues: InvoiceHistorySearchFilterValues;
  setSelectedFilterValues: React.Dispatch<React.SetStateAction<InvoiceHistorySearchFilterValues>>;
}

const InvoiceHistorySearchFilter = ({
  resetSearchFilter,
  fetchSearchData,
  selectedFilterValues,
  setSelectedFilterValues,
}: Props) => {
  const onChangeSelectedPayStatus =
    (status: InvoicePaymentStatus) =>
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setSelectedFilterValues(prev => ({
        ...prev,
        payStatus: {
          ...prev.payStatus,
          // @ts-ignore
          [value]: !prev.payStatus[status], // toggle
        },
      }));
    };

  const onChangeSelectedPlanType =
    (plan: GroupPlanType) =>
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setSelectedFilterValues(prev => ({
        ...prev,
        planType: {
          ...prev.planType,
          // @ts-ignore
          [value]: !prev.planType[plan], // toggle
        },
      }));
    };

  const onChangeSelectedPaymentMethod =
    (method: PaymentMethod) =>
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setSelectedFilterValues(prev => ({
        ...prev,
        paymentMethod: {
          ...prev.paymentMethod,
          [value]: !prev.paymentMethod[method], // toggle
        },
      }));
    };

  const onChangeSelectedDateInterval = (value: string[]) =>
    setSelectedFilterValues(prev => ({
      ...prev,
      dateInterval: value,
    }));

  const onChangeSelectedDateShortCut = (value: number) =>
    setSelectedFilterValues(prev => ({
      ...prev,
      dateShortCut: value,
    }));

  const onChangeKeyword = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setSelectedFilterValues(prev => ({ ...prev, keyword: value }));
  };

  const onKeyDownKeyword = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const inputKey = keyboardKey.getCode(e);
    if (inputKey === keyboardKey.Enter) {
      fetchSearchData();
    }
  };

  return (
    <FilterLayout resetFilter={resetSearchFilter} getSearchResult={fetchSearchData}>
      <SearchContentBlock>
        <DateRangeFilter
          label={'Date'}
          dateInterval={selectedFilterValues.dateInterval}
          setDateInterval={onChangeSelectedDateInterval as Dispatch<SetStateAction<string[]>>}
          selectedShortcut={selectedFilterValues.dateShortCut}
          setSelectedShortcut={
            onChangeSelectedDateShortCut as Dispatch<SetStateAction<SearchDateTerms | null>>
          }
        />
        <FilterElement label={'Plan Type'}>
          <GroupPlanTypeFilter
            selectedPlanTypes={selectedFilterValues.planType}
            onChangeSelectedGroupPlan={onChangeSelectedPlanType}
          />
        </FilterElement>
        <FilterElement label={'Payment Method'}>
          <PayMethodFilters
            paymentMethod={selectedFilterValues.paymentMethod}
            onChangePaymentMethod={onChangeSelectedPaymentMethod}
          />
        </FilterElement>
        <FilterElement label={'Payment Status'}>
          <PayStatusFilters
            payStatus={selectedFilterValues.payStatus}
            onChangePayStatus={onChangeSelectedPayStatus}
          />
        </FilterElement>
        <FilterElement label={'Keyword'}>
          <KeywordInput
            keyword={selectedFilterValues.keyword}
            onChangeKeyword={onChangeKeyword}
            onKeyDownKeyword={onKeyDownKeyword}
            placeholder={'Invoice No, Group ID, Group Name, Admin Email, Domain'}
            maxLength={200}
          />
        </FilterElement>
      </SearchContentBlock>
    </FilterLayout>
  );
};

export default InvoiceHistorySearchFilter;
